import { getLocaleString } from '../assets/i18n/config';
import { isCurrencySymbolAfter } from './moneyFormatting';

export const formatCompactNumber = (
  value: number,
  options: {
    isCurrency?: boolean;
    isPercentage?: boolean;
    decimals?: number;
  } = {},
) => {
  const { isCurrency = false, isPercentage = false, decimals = 2 } = options;
  const locale = getLocaleString();
  const symbolAfter = isCurrencySymbolAfter(locale);

  if (value === 0) {
    if (isCurrency) {
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(0);
      return symbolAfter ? `${formatted} $` : `$${formatted}`;
    }
    return '0';
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  let suffix = '';
  let formattedValue = absValue;

  if (absValue >= 1e12) {
    suffix = 'T';
    formattedValue = absValue / 1e12;
  } else if (absValue >= 1e9) {
    suffix = 'B';
    formattedValue = absValue / 1e9;
  } else if (absValue >= 1e6) {
    suffix = 'M';
    formattedValue = absValue / 1e6;
  } else if (absValue >= 1e3) {
    suffix = 'K';
    formattedValue = absValue / 1e3;
  }

  const localizedNumStr = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(formattedValue);

  if (isCurrency) {
    return symbolAfter
      ? `${sign}${localizedNumStr}${suffix} $`
      : `${sign}$${localizedNumStr}${suffix}`;
  }

  if (isPercentage) {
    return `${sign}${localizedNumStr}%`;
  }

  return `${sign}${localizedNumStr}${suffix}`;
};

export const generateComparativeValue = (
  currentValue: number | undefined,
  previousValue: number | undefined,
  isCurrency = true,
  isPercentage = false,
  timePeriod = 'from last month',
) => {
  if (currentValue === undefined || previousValue === undefined) {
    return null;
  }

  const change = currentValue - previousValue;
  const percentageChange =
    previousValue !== 0 ? (change / previousValue) * 100 : 0;
  const isIncrease = change >= 0;

  const formattedValue = formatCompactNumber(currentValue, {
    isCurrency,
    isPercentage,
    decimals: isCurrency ? 2 : 0,
  });
  const formattedChange = formatCompactNumber(Math.abs(change), {
    isCurrency,
    isPercentage,
    decimals: isCurrency ? 2 : 0,
  });
  const formattedPercentage = `${Math.abs(percentageChange).toFixed(2)}%`;

  const arrow = isIncrease ? '↑' : '↓';

  return {
    value: formattedValue,
    isIncrease,
    changeText: `${arrow} ${formattedChange} (${formattedPercentage}) ${timePeriod}`,
  };
};

/**
 * Converts a color object with RGB and alpha values to hex format.
 * Returns 6-character hex for full opacity or 8-character hex with alpha for transparency.
 *
 * @param color - Object containing hex string and rgb object with r, g, b, and optional a values
 * @returns Hex color string with optional alpha channel (e.g., '#FF0000' or '#FF000080')
 */
export const colorToHexWithAlpha = (color: {
  hex: string;
  rgb: { r: number; g: number; b: number; a?: number };
}): string => {
  // If alpha is less than 1, append it to the hex color
  if (color.rgb.a !== undefined && color.rgb.a < 1) {
    const alpha = Math.round(color.rgb.a * 255);
    const alphaHex = alpha.toString(16).padStart(2, '0');
    return `${color.hex}${alphaHex}`;
  }
  return color.hex;
};
